import React, { useCallback, useEffect, useMemo, useState } from 'react';
import './controlCenter.css';

const REMOTE_LOGIN_URL = 'https://remotedesktop.google.com/access';
const MISSED_GRACE_MS = 15 * 60 * 1000;

const SCHEDULE = [
  { label: '6:10 AM', hour: 6, minute: 10 },
  { label: '8:10 AM', hour: 8, minute: 10 },
  { label: '10:10 AM', hour: 10, minute: 10 },
  { label: '12:10 PM', hour: 12, minute: 10 },
  { label: '2:10 PM', hour: 14, minute: 10 },
  { label: '4:10 PM', hour: 16, minute: 10 },
  { label: '6:10 PM', hour: 18, minute: 10 },
  { label: '8:10 PM', hour: 20, minute: 10 },
  { label: '10:10 PM', hour: 22, minute: 10 }
];

function formatDateTime(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  }).format(date);
}

function sameLocalDay(a, b) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

function scheduledDate(slot, base = new Date()) {
  return new Date(base.getFullYear(), base.getMonth(), base.getDate(), slot.hour, slot.minute, 0, 0);
}

function closestScheduleIndex(value) {
  if (!value) return -1;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return -1;

  let bestIndex = -1;
  let bestDistance = Number.POSITIVE_INFINITY;

  SCHEDULE.forEach((slot, index) => {
    const target = scheduledDate(slot, date);
    const distance = Math.abs(date.getTime() - target.getTime());
    if (distance < bestDistance) {
      bestDistance = distance;
      bestIndex = index;
    }
  });

  return bestDistance <= 45 * 60 * 1000 ? bestIndex : -1;
}

function scheduleIndexFromSlotKey(value) {
  const match = String(value || '').match(/_(\d{2}):(\d{2})$/);
  if (!match) return -1;
  const hour = Number(match[1]);
  const minute = Number(match[2]);
  return SCHEDULE.findIndex(slot => slot.hour === hour && slot.minute === minute);
}

function applyAuthoritativeScheduleHistory(runs, status, now) {
  const history = status?.scheduleHistory;
  if (!Array.isArray(history)) return false;

  history.forEach(record => {
    const index = scheduleIndexFromSlotKey(record?.slotKey);
    if (index < 0) return;

    const recordStatus = String(record?.status || '').toLowerCase();
    if (recordStatus === 'started') {
      runs[index].state = 'running';
      runs[index].result = 'RUNNING';
    } else if (recordStatus === 'success') {
      runs[index].state = 'success';
      runs[index].result = `${Number(record?.files || 30)}/30`;
    } else if (recordStatus === 'failed') {
      runs[index].state = 'failed';
      runs[index].result = 'FAILED';
    } else if (recordStatus === 'login') {
      runs[index].state = 'warn';
      runs[index].result = 'LOGIN';
    } else if (recordStatus === 'missed') {
      runs[index].state = 'missed';
      runs[index].result = 'MISSED';
    }
  });

  // A missing record is the only remaining MISSED condition. The controller's
  // persistent history wins for every real result, so restarts/updates cannot
  // erase earlier successful runs and manufacture false misses.
  const canJudgeMissed = Boolean(status?.connected) && status?.automationEnabled !== false;
  runs.forEach((run, index) => {
    if (run.state !== 'unknown') return;
    const cutoff = scheduledDate(SCHEDULE[index], now).getTime() + MISSED_GRACE_MS;
    if (canJudgeMissed && now.getTime() >= cutoff) {
      run.state = 'missed';
      run.result = 'MISSED';
    }
  });

  return true;
}

function buildScheduleRuns(status) {
  const now = new Date();
  const runs = SCHEDULE.map(slot => ({
    ...slot,
    state: scheduledDate(slot, now) > now ? 'upcoming' : 'unknown',
    result: scheduledDate(slot, now) > now ? 'UPCOMING' : 'NO DATA'
  }));

  if (applyAuthoritativeScheduleHistory(runs, status, now)) return runs;

  // Compatibility path for a controller older than v2.2.13. Once the new
  // controller checks in, scheduleHistory above is the single source of truth.
  const activity = Array.isArray(status?.activity) ? status.activity : [];
  const chronological = activity
    .map(item => ({ ...item, parsedTime: new Date(item.time) }))
    .filter(item => !Number.isNaN(item.parsedTime.getTime()) && sameLocalDay(item.parsedTime, now))
    .sort((a, b) => a.parsedTime - b.parsedTime);

  let activeScheduledIndex = -1;

  chronological.forEach(item => {
    const title = String(item.title || '').toLowerCase();
    const detail = String(item.detail || '').toLowerCase();

    if (title.includes('live update started') && detail.includes('scheduled')) {
      const index = closestScheduleIndex(item.parsedTime);
      if (index >= 0) {
        runs[index].state = 'running';
        runs[index].result = 'RUNNING';
        activeScheduledIndex = index;
      }
      return;
    }

    if (activeScheduledIndex < 0) return;

    if (title.includes('update completed')) {
      const match = String(item.detail || '').match(/\b\d+\/30\b/);
      runs[activeScheduledIndex].state = 'success';
      runs[activeScheduledIndex].result = match ? match[0] : 'COMPLETE';
      activeScheduledIndex = -1;
    } else if (title.includes('update failed')) {
      runs[activeScheduledIndex].state = 'failed';
      runs[activeScheduledIndex].result = 'FAILED';
      activeScheduledIndex = -1;
    } else if (title.includes('login required')) {
      runs[activeScheduledIndex].state = 'warn';
      runs[activeScheduledIndex].result = 'LOGIN';
      activeScheduledIndex = -1;
    }
  });

  if (status?.currentRun?.trigger === 'schedule') {
    const index = closestScheduleIndex(status.currentRun.requestedAt);
    if (index >= 0) {
      runs[index].state = 'running';
      runs[index].result = 'RUNNING';
    }
  }

  if (status?.lastRun?.trigger === 'schedule' && status.lastRun.completedAt) {
    const index = closestScheduleIndex(status.lastRun.completedAt);
    if (index >= 0 && runs[index].state !== 'running') {
      const succeeded = status.lastRun.status === 'success';
      runs[index].state = succeeded ? 'success' : 'failed';
      runs[index].result = succeeded ? `${status.lastRun.files || 30}/30` : 'FAILED';
    }
  }

  return runs;
}

const SCHEDULE_STYLE = {
  upcoming: { borderColor: 'rgba(255,255,255,.09)', background: 'rgba(255,255,255,.025)', color: '#93a79e' },
  unknown: { borderColor: 'rgba(255,255,255,.07)', background: 'rgba(255,255,255,.018)', color: '#667a70' },
  running: { borderColor: 'rgba(101,169,255,.36)', background: 'rgba(101,169,255,.10)', color: '#9fc8ff', boxShadow: 'inset 0 0 0 1px rgba(101,169,255,.05)' },
  success: { borderColor: 'rgba(73,209,125,.34)', background: 'rgba(73,209,125,.095)', color: '#8de3ac' },
  failed: { borderColor: 'rgba(255,107,103,.34)', background: 'rgba(255,107,103,.09)', color: '#ff9894' },
  missed: { borderColor: 'rgba(255,107,103,.42)', background: 'rgba(255,107,103,.10)', color: '#ff9894', boxShadow: 'inset 0 0 0 1px rgba(255,107,103,.04)' },
  warn: { borderColor: 'rgba(242,173,75,.34)', background: 'rgba(242,173,75,.09)', color: '#f6c677' }
};

function stateDetails(status) {
  const state = String(status?.state || 'offline').toLowerCase();
  if (state === 'idle') return { tone: 'good', eyebrow: 'SYSTEM HEALTHY', title: 'Idle & ready', detail: 'Waiting for the next scheduled TeleStaff update.' };
  if (state === 'running') return { tone: 'working', eyebrow: 'UPDATE IN PROGRESS', title: 'TeleStaff is running', detail: 'Capturing and validating the current 7-day staffing snapshot.' };
  if (state === 'waiting-for-login' || state === 'auth-required') return { tone: 'warn', eyebrow: 'ACTION NEEDED', title: 'Metro login required', detail: 'Automation is paused. The last good staffing snapshot remains live.' };
  if (state === 'restarting-browser' || state === 'restarting-controller') return { tone: 'working', eyebrow: 'RECOVERY IN PROGRESS', title: 'Restarting controller services', detail: 'The Windows controller is performing the requested recovery action.' };
  if (state === 'browser-closed') return { tone: 'bad', eyebrow: 'BROWSER OFFLINE', title: 'Resident browser closed', detail: 'Restart the TeleStaff browser to restore automation.' };
  if (state === 'paused') return { tone: 'muted', eyebrow: 'AUTOMATION PAUSED', title: 'Scheduled runs are paused', detail: 'Use Resume Automation when you are ready to continue.' };
  if (state === 'failed') return { tone: 'bad', eyebrow: 'RUN FAILED', title: 'Automation needs attention', detail: status?.lastError || 'The last attempt failed. Previous live files were preserved.' };
  return { tone: 'offline', eyebrow: 'CONTROLLER OFFLINE', title: 'Windows controller is not connected', detail: status?.lastError || 'Waiting for the laptop heartbeat.' };
}

function Icon({ name }) {
  const common = { width: 21, height: 21, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 1.9, strokeLinecap: 'round', strokeLinejoin: 'round', 'aria-hidden': true };
  if (name === 'play') return <svg {...common}><path d="M8 5v14l11-7z" /></svg>;
  if (name === 'retry') return <svg {...common}><path d="M20 6v5h-5"/><path d="M19 11a7.5 7.5 0 1 0 1 5"/></svg>;
  if (name === 'pause') return <svg {...common}><path d="M8 5v14M16 5v14"/></svg>;
  if (name === 'browser') return <svg {...common}><rect x="3" y="4" width="18" height="16" rx="2"/><path d="M3 9h18"/><path d="M7 6.5h.01M10 6.5h.01"/></svg>;
  if (name === 'restart') return <svg {...common}><path d="M4 12a8 8 0 1 0 2.3-5.7L4 8.6"/><path d="M4 4v4.6h4.6"/></svg>;
  if (name === 'lock') return <svg {...common}><rect x="5" y="10" width="14" height="10" rx="2"/><path d="M8 10V7a4 4 0 0 1 8 0v3"/></svg>;
  if (name === 'refresh') return <svg {...common}><path d="M20 11a8 8 0 1 0-2 5.3"/><path d="M20 5v6h-6"/></svg>;
  if (name === 'bell') return <svg {...common}><path d="M18 8a6 6 0 0 0-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9"/><path d="M10 21h4"/></svg>;
  if (name === 'external') return <svg {...common}><path d="M14 3h7v7"/><path d="M10 14 21 3"/><path d="M21 14v5a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5"/></svg>;
  return null;
}

function Metric({ label, value, sub, tone }) {
  return (
    <div className="cc-metric">
      <div className="cc-metric-label">{label}</div>
      <div className={`cc-metric-value ${tone ? `is-${tone}` : ''}`}>{value || '—'}</div>
      {sub && <div className="cc-metric-sub">{sub}</div>}
    </div>
  );
}

function LoginPanel({ onAuthenticated }) {
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const submit = async event => {
    event.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      const response = await fetch('/api/control-auth', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ password })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.authenticated) throw new Error(data.error || 'Incorrect password.');
      setPassword('');
      onAuthenticated();
    } catch (err) {
      setError(err?.message || 'Unable to sign in.');
      setPassword('');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="cc-login-shell">
      <section className="cc-login-card">
        <div className="cc-brand-mark"><span>TS</span></div>
        <div className="cc-login-kicker">SMFD LINE MEDIC PROGRAM</div>
        <h1>TeleStaff Control</h1>
        <p>Protected access to the staffing automation controller.</p>
        <form onSubmit={submit} className="cc-login-form">
          <label htmlFor="control-password">Control password</label>
          <div className="cc-password-wrap">
            <input id="control-password" type={showPassword ? 'text' : 'password'} value={password} onChange={event => setPassword(event.target.value)} autoComplete="current-password" autoFocus required placeholder="Enter password" />
            <button type="button" className="cc-show-password" onClick={() => setShowPassword(value => !value)}>{showPassword ? 'Hide' : 'Show'}</button>
          </div>
          {error && <div className="cc-login-error">{error}</div>}
          <button className="cc-login-button" type="submit" disabled={submitting}>{submitting ? 'Signing in…' : 'Open Control Center'}</button>
        </form>
        <div className="cc-secure-note"><Icon name="lock" /><span>Server-side password · secure session cookie</span></div>
      </section>
    </main>
  );
}

function urlBase64ToUint8Array(value) {
  const padding = '='.repeat((4 - value.length % 4) % 4);
  const base64 = (value + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  return Uint8Array.from([...raw].map(char => char.charCodeAt(0)));
}

export default function ControlCenter() {
  const [authState, setAuthState] = useState('checking');
  const [status, setStatus] = useState(null);
  const [statusError, setStatusError] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [commandBusy, setCommandBusy] = useState('');
  const [notice, setNotice] = useState('');
  const [pushState, setPushState] = useState('unknown');

  const loadStatus = useCallback(async (quiet = false) => {
    if (!quiet) setRefreshing(true);
    try {
      const response = await fetch('/api/control-status', { cache: 'no-store', credentials: 'same-origin' });
      if (response.status === 401) {
        setAuthState('signed-out');
        setStatus(null);
        return;
      }
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Unable to load controller status.');
      setStatus(data);
      setStatusError('');
      setAuthState('signed-in');
    } catch (err) {
      setStatusError(err?.message || 'Unable to load controller status.');
    } finally {
      if (!quiet) setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    const check = async () => {
      try {
        const response = await fetch('/api/control-session', { cache: 'no-store', credentials: 'same-origin' });
        const data = await response.json().catch(() => ({}));
        if (cancelled) return;
        if (response.ok && data.authenticated) {
          setAuthState('signed-in');
          loadStatus(true);
        } else {
          setAuthState('signed-out');
        }
      } catch (_) {
        if (!cancelled) setAuthState('signed-out');
      }
    };
    check();
    return () => { cancelled = true; };
  }, [loadStatus]);

  useEffect(() => {
    if (authState !== 'signed-in') return undefined;
    const timer = window.setInterval(() => loadStatus(true), 5000);
    return () => window.clearInterval(timer);
  }, [authState, loadStatus]);

  useEffect(() => {
    if (authState !== 'signed-in' || !('serviceWorker' in navigator) || !('PushManager' in window)) return;
    navigator.serviceWorker.getRegistration('/').then(async registration => {
      if (!registration) return;
      const subscription = await registration.pushManager.getSubscription();
      if (subscription) setPushState('enabled');
      else setPushState('available');
    }).catch(() => {});
  }, [authState]);

  const details = useMemo(() => stateDetails(status), [status]);
  const scheduleRuns = useMemo(() => buildScheduleRuns(status), [status]);
  const connected = Boolean(status?.connected);
  const automationEnabled = status?.automationEnabled !== false;
  const running = String(status?.state || '').toLowerCase() === 'running';

  const logout = async () => {
    await fetch('/api/control-logout', { method: 'POST', credentials: 'same-origin' }).catch(() => {});
    setStatus(null);
    setAuthState('signed-out');
  };

  const openRemoteLogin = () => {
    window.open(REMOTE_LOGIN_URL, '_blank', 'noopener,noreferrer');
  };

  const sendCommand = async command => {
    if (!connected) return;
    if (['restart-browser', 'restart-controller'].includes(command)) {
      const label = command === 'restart-browser' ? 'restart the resident TeleStaff browser' : 'restart the Windows controller';
      if (!window.confirm(`Are you sure you want to ${label}?`)) return;
    }

    setCommandBusy(command);
    setNotice('');
    try {
      const response = await fetch('/api/control-command', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ command })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.ok) throw new Error(data.error || 'Controller command was rejected.');
      setNotice(`${command.replaceAll('-', ' ')} sent to the laptop.`);
      window.setTimeout(() => loadStatus(true), 1200);
    } catch (err) {
      setStatusError(err?.message || 'Unable to send controller command.');
    } finally {
      setCommandBusy('');
    }
  };

  const enableNotifications = async () => {
    setNotice('');
    try {
      if (!('serviceWorker' in navigator) || !('PushManager' in window) || !('Notification' in window)) {
        throw new Error('Push notifications are not available in this browser. On iPhone, add /control to the Home Screen and open it from there.');
      }

      const registration = await navigator.serviceWorker.register('/control-sw.js', { scope: '/' });
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') throw new Error('Notification permission was not granted.');

      const keyResponse = await fetch('/api/control-push-key', { credentials: 'same-origin', cache: 'no-store' });
      const keyData = await keyResponse.json().catch(() => ({}));
      if (!keyResponse.ok || !keyData.publicKey) throw new Error(keyData.error || 'Push notifications are not configured yet.');

      let subscription = await registration.pushManager.getSubscription();
      if (!subscription) {
        subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(keyData.publicKey)
        });
      }

      const saveResponse = await fetch('/api/control-push-subscribe', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ subscription: subscription.toJSON() })
      });
      const saveData = await saveResponse.json().catch(() => ({}));
      if (!saveResponse.ok || !saveData.ok) throw new Error(saveData.error || 'Unable to save notification subscription.');

      setPushState('enabled');
      setNotice('TeleStaff alerts are enabled on this device.');
    } catch (err) {
      setPushState('error');
      setStatusError(err?.message || 'Unable to enable notifications.');
    }
  };

  if (authState === 'checking') return <main className="cc-loading"><div className="cc-spinner"/><span>Opening control center…</span></main>;
  if (authState !== 'signed-in') return <LoginPanel onAuthenticated={() => { setAuthState('signed-in'); loadStatus(); }} />;

  return (
    <main className="cc-shell">
      <div className="cc-page">
        <header className="cc-header">
          <div className="cc-brand"><div className="cc-brand-mark small"><span>TS</span></div><div><div className="cc-header-kicker">SMFD LINE MEDIC PROGRAM</div><h1>TeleStaff Control</h1></div></div>
          <div className="cc-header-actions">
            <button className={`cc-notify ${pushState === 'enabled' ? 'is-on' : ''}`} onClick={enableNotifications} title="Enable TeleStaff push alerts"><Icon name="bell"/><span>{pushState === 'enabled' ? 'Alerts On' : 'Enable Alerts'}</span></button>
            <button className="cc-icon-button" onClick={() => loadStatus()} disabled={refreshing} aria-label="Refresh status"><Icon name="refresh" /></button>
            <button className="cc-logout" onClick={logout}>Sign out</button>
          </div>
        </header>

        <section className={`cc-hero is-${details.tone}`}>
          <div className="cc-hero-top"><div><div className="cc-eyebrow"><span className="cc-status-dot" />{details.eyebrow}</div><h2>{details.title}</h2><p>{details.detail}</p></div><div className={`cc-state-pill is-${details.tone}`}>{String(status?.state || 'OFFLINE').replaceAll('-', ' ').toUpperCase()}</div></div>
          {!connected && <div className="cc-setup-banner"><strong>Laptop heartbeat is offline.</strong><span>Scheduled TeleStaff automation may still be running locally, but remote commands require the Windows controller connection.</span></div>}
        </section>

        {notice && <div className="cc-success-banner">{notice}</div>}
        {statusError && <div className="cc-error-banner">{statusError}</div>}

        <section className="cc-metrics-grid">
          <Metric label="Last Successful Update" value={formatDateTime(status?.lastSuccessAt)} sub={status?.lastSuccessFiles ? `${status.lastSuccessFiles}/30 files` : 'Waiting for controller'} />
          <Metric label="Next Scheduled Run" value={automationEnabled ? formatDateTime(status?.nextScheduledRun) : 'Paused'} sub="Windows local time" />
          <Metric label="TeleStaff Session" value={status?.authenticated ? 'Authenticated' : connected ? 'Login Required' : 'Not connected'} tone={status?.authenticated ? 'good' : connected ? 'warn' : ''} />
          <Metric label="Resident Browser" value={status?.browserOpen ? 'Online' : connected ? 'Offline' : 'Not connected'} tone={status?.browserOpen ? 'good' : connected ? 'bad' : ''} />
        </section>

        <section className="cc-card">
          <div className="cc-section-heading"><div><div className="cc-section-kicker">REMOTE ACTIONS</div><h3>Controller</h3></div><span className={`cc-mini-state ${automationEnabled ? 'is-on' : ''}`}>{automationEnabled ? 'AUTOMATION ON' : 'AUTOMATION PAUSED'}</span></div>
          <div className="cc-action-grid">
            <button className="cc-action primary" disabled={!connected || running || Boolean(commandBusy)} onClick={() => sendCommand('run-live')}><span className="cc-action-icon"><Icon name="play" /></span><span><strong>{commandBusy === 'run-live' ? 'Sending…' : 'Run Now'}</strong><small>Start a live 30-file update</small></span></button>
            <button className="cc-action" disabled={!connected || running || Boolean(commandBusy)} onClick={() => sendCommand('retry')}><span className="cc-action-icon"><Icon name="retry" /></span><span><strong>Retry Last Run</strong><small>Repeat a live update</small></span></button>
            <button className="cc-action" disabled={!connected || Boolean(commandBusy)} onClick={() => sendCommand(automationEnabled ? 'pause' : 'resume')}><span className="cc-action-icon"><Icon name="pause" /></span><span><strong>{automationEnabled ? 'Pause Automation' : 'Resume Automation'}</strong><small>Control scheduled updates</small></span></button>
            <button className="cc-action" onClick={openRemoteLogin}><span className="cc-action-icon"><Icon name="external" /></span><span><strong>Remote Login</strong><small>Open laptop to complete Okta / MFA</small></span></button>
            <button className="cc-action" disabled={!connected || running || Boolean(commandBusy)} onClick={() => sendCommand('restart-browser')}><span className="cc-action-icon"><Icon name="browser" /></span><span><strong>Restart Browser</strong><small>Recover resident Chromium</small></span></button>
            <button className="cc-action danger" disabled={!connected || running || Boolean(commandBusy)} onClick={() => sendCommand('restart-controller')}><span className="cc-action-icon"><Icon name="restart" /></span><span><strong>Restart Controller</strong><small>Supervisor brings it back</small></span></button>
          </div>
        </section>

        <section className="cc-card">
          <div className="cc-section-heading compact"><div><div className="cc-section-kicker">AUTOMATIC LIVE UPDATES</div><h3>Daily schedule</h3></div><span className="cc-timezone">LOCAL TIME</span></div>
          <div className="cc-schedule">
            {scheduleRuns.map(run => (
              <span
                key={run.label}
                title={run.state === 'missed' ? 'Scheduled run did not start within 15 minutes of its scheduled time.' : undefined}
                style={{
                  ...SCHEDULE_STYLE[run.state],
                  minWidth: 82,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 2,
                  transition: 'background .2s ease, border-color .2s ease, color .2s ease'
                }}
              >
                <strong style={{ color: 'inherit', fontSize: 11 }}>{run.label}</strong>
                <small style={{ color: 'inherit', fontSize: 8, fontWeight: 850, letterSpacing: '.08em', opacity: .92 }}>{run.result}</small>
              </span>
            ))}
          </div>
        </section>

        <section className="cc-card"><div className="cc-section-heading compact"><div><div className="cc-section-kicker">RECENT ACTIVITY</div><h3>Automation log</h3></div></div><div className="cc-activity-list">{(status?.activity || []).length > 0 ? status.activity.map((item, index) => <div className="cc-activity" key={`${item.time || 'activity'}-${index}`}><span className={`cc-activity-dot is-${item.tone || 'neutral'}`} /><div><strong>{item.title}</strong><small>{item.detail}</small></div><time>{formatDateTime(item.time)}</time></div>) : <div className="cc-empty-activity"><span className="cc-activity-dot is-neutral"/><div><strong>No recent activity</strong><small>Controller events will appear here as the laptop checks in.</small></div></div>}</div></section>

        <footer className="cc-footer"><Icon name="lock"/><span>Protected control surface · no Metro password or MFA secrets are stored here</span></footer>
      </div>
    </main>
  );
}
